/* eslint-disable react/require-default-props */
import { useEffect, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { Spinner, util as W3Util, logging } from 'w3-user-ui-component';

import { useAvatarManifest } from '../../assets/data/avatarManifest';
import { AvatarAppearanceV1 } from '../../utils/interfaces/DbInterfaces';
import CompanionCanvas from '../CompanionCanvas/CompanionCanvas';

import styles from './ProfilePicture.module.scss';

export interface IProfilePicture {
  className?: string;
  nick: string;
  firstName?: string;
  lastName?: string;
  srcURL?: string;
  fadeIn?: boolean;
  mood?: string;
  avatar?: AvatarAppearanceV1;
}

const ProfilePicture = ({ className = undefined, srcURL = undefined, nick, firstName, lastName, fadeIn = false, mood, avatar }: IProfilePicture) => {
  const [imageURL, setImageURL] = useState<string | undefined>();
  useEffect(() => {
    setImageURL(srcURL ? `${W3Util.getPublicProfileUrl()}/${srcURL}?key=${new Date().getTime()}` : undefined);
  }, [srcURL]);

  const [hasPicture, setHasPicture] = useState(false);
  useEffect(() => {
    setHasPicture(!!imageURL);
  }, [imageURL]);

  const [loadingImage, setLoadingImage] = useState(false);
  useEffect(() => {
    if (hasPicture && !avatar) {
      setLoadingImage(true);
    }
  }, [hasPicture, avatar]);

  const [zoomImage, setZoomImage] = useState<number>(0);
  const [errorLoadingImage, setErrorLoadingImage] = useState(false);
  const [initials, setInitials] = useState<string | undefined>();
  const avatarManifest = useAvatarManifest();

  useEffect(() => {
    if (firstName && lastName) {
      setInitials((firstName.charAt(0) + lastName.charAt(0)).toUpperCase());
    } else {
      setInitials(undefined);
    }
  }, [firstName, lastName]);

  const onPictureLoaded = (event: any) => {
    logging.logDebug('ProfilePicture -> onPictureLoaded: event', nick);
    setLoadingImage(false);

    const imageData = {
      zoom: {
        width: event.target.clientWidth / event.target.clientHeight,
        height: event.target.clientHeight / event.target.clientWidth,
      },
    };

    if (imageData.zoom.width > 1) {
      setZoomImage(imageData.zoom.width);
    } else if (imageData.zoom.height > 1) {
      setZoomImage(imageData.zoom.height);
    } else {
      setZoomImage(1);
    }
  };

  const onErrorLoadingPicture = (event: any) => {
    logging.logDebug('ProfilePicture -> onErrorLoadingPicture: event', event);
    setLoadingImage(false);
    setErrorLoadingImage(true);
  };

  // If avatar is provided, render the avatar instead
  if (avatar) {
    const moodSrc: string = String(avatar.layers.mood);
    return (
      <div
        className={`${styles.profile_picture} ${className || ''} ${styles.avatar_mode} ${fadeIn ? styles.fade_in : ''}`}
        aria-label="User's avatar"
        role='img'
      >
        <div className={styles.picture_container}>
          <CompanionCanvas appearance={avatar} width={120} height={120} />
          {avatar.layers.mood && avatarManifest[avatar.layers.mood] && (
            <Box
              position='absolute'
              top='-10px'
              right='-10px'
              bg='white'
              borderRadius='16px 16px 16px 0'
              border='1px solid #F3F3F3'
              // boxShadow='0 2px 8px rgba(0,0,0,0.06)'
              p='8px'
              zIndex={2}
              minW='28px'
              minH='28px'
              display='flex'
              alignItems='center'
              justifyContent='center'
              // _dark={{
              //   bg: 'gray.500',
              //   border: '1px solid #282A35',
              // }}
            >
              <img src={avatarManifest[avatar.layers.mood].svg} alt={moodSrc} width={24} height={24} />
            </Box>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.profile_picture} ${className || ''} ${zoomImage ? styles.zoom : ''}  ${
        !hasPicture || errorLoadingImage ? styles.no_image : ''
      } ${fadeIn ? styles.fade_in : ''}`}
      aria-label="User's profile picture"
      role='img'
    >
      <div className={styles.picture_container}>
        {hasPicture && !errorLoadingImage ? (
          <img
            onLoad={onPictureLoaded}
            onError={onErrorLoadingPicture}
            className={`${styles.image}${loadingImage ? ` ${styles.hide}` : ''}${fadeIn ? ` ${styles.fade_in}` : ''}`}
            src={imageURL}
            style={{ transform: `scale(${zoomImage})` }}
            alt='user'
          />
        ) : (
          <div className={styles.initials}>{initials}</div>
        )}

        {loadingImage && !errorLoadingImage && (
          <div className={styles.spinner_wrapper}>
            <Spinner variant='dark' />
          </div>
        )}
      </div>

      {mood && <div className={styles.mood}>{mood}</div>}
    </div>
  );
};

export default ProfilePicture;
